/**
 * 人民币参考金额的换算。
 *
 * 🔴 **这只是展示用的估算，不是资金数据。** Airwallex 换汇用的是它自己在成交那一刻的
 * 汇率，与这里的第三方公开汇率必然有差。任何把它当作应付/实付金额的用法都是错的，
 * 所以：
 * - 它永远以「≈」和次要样式出现，旁边标明「参考」；
 * - 它绝不回传给服务端，也绝不参与任何提交；
 * - 真正的金额始终是那个字符串（见 api/payouts.ts 顶部说明）。
 *
 * 也正因为是估算，这里可以用 JavaScript 浮点做除法 —— 参考值差 0.01 分无所谓。
 * 真实金额仍然全程字符串，两者不要混。
 */
export const CNY = 'CNY';
export const USD = 'USD';

/**
 * 任意两个币种之间的参考换算。汇率表 base=CNY，所以先折回人民币再折过去。
 *
 * @param amount 金额字符串（如 "88.8"）
 * @param from 源币种（三字母）
 * @param to 目标币种（三字母）
 * @param rates base=CNY 的汇率表，`rates[X]` = 1 CNY 能换多少 X
 * @returns 估算值；两边同币种、汇率缺失或金额非法时返回 null
 */
export function convert(
  amount: string,
  from: string | null | undefined,
  to: string,
  rates: Record<string, number> | null,
): number | null {
  if (!from || !to) return null;
  // 同币种不换算：在「100 USD」下面再写一行「≈ $100」是纯噪音
  if (from === to) return null;
  if (!rates) return null;
  // 汇率表本身就是以 CNY 为基准的，表里有没有 CNY 这一项都不影响
  const fromRate = from === CNY ? 1 : rates[from];
  const toRate = to === CNY ? 1 : rates[to];
  if (!fromRate || fromRate <= 0 || !toRate || toRate <= 0) return null;
  // 🔴 必须先挡掉空串：`Number('')` 是 0 且是有限数，直接算下去会显示「≈ ¥0.00」——
  // 一个看起来煞有介事的零，比什么都不显示危险得多。
  // 同理挡掉 <= 0：金额在库里有 CHECK (amount > 0)，出现非正数说明数据已经不对了，
  // 这时更不该给出一个像模像样的参考值。
  const trimmed = amount?.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return (n / fromRate) * toRate;
}

/**
 * @returns 人民币估算值；币种就是 CNY、汇率缺失或金额非法时返回 null
 */
export function toCNY(
  amount: string,
  currency: string | null | undefined,
  rates: Record<string, number> | null,
): number | null {
  return convert(amount, currency, CNY, rates);
}

/** 只有参考展示会用到的币种才需要符号；其余原样带三字母码。 */
const SYMBOL: Record<string, string> = { CNY: '¥', USD: '$' };

/** 格式化成「¥1,234.56」「$1,234.56」。参考值统一保留两位。 */
export function formatMoney(value: number, currency: string): string {
  const digits = value.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const symbol = SYMBOL[currency];
  return symbol ? `${symbol}${digits}` : `${digits} ${currency}`;
}

/** 格式化成「¥1,234.56」。参考值统一保留两位。 */
export function formatCNY(value: number): string {
  return formatMoney(value, CNY);
}
